import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import Loader from '../../../components/Loader'
import Message from '../../../components/Message'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import Input from '../../../components/v2/Input'
import MovingPrice from '../../../components/v2/MovingPrice'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import Checkbox from '../../../components/v2/buttons/Checkbox'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import TplProgressbar from '../../../components/v2/progrees-bars/TplProgressbar'
import { useAppDispatch, useAppSelector } from '../../../store'
import {
  TplState,
  clearTplCarInfo,
  setCarFields,
  setCheckMyCar,
  setTplInsuranceCarInfo,
  setUpdateCarCountdown,
} from '../../../store/tplSlice'
import { Helmet } from 'react-helmet-async'
import { AppState } from '../../../store/appSlice'

const TplCarInfo = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  const { data: tplPageInfo, isLoading: isTplCarPageInfoLoading } = api.useGetTplCarPageInfoQuery(undefined)

  const pageTitle = tplPageInfo?.translations[lang]?.title
  const stepName = tplPageInfo?.translations[lang]?.stepName

  const { screenHeader, checkout, filter } = useAppSelector((data: { tpl: TplState }) => data.tpl)
  const insuredIdNumber =
    checkout.forWho === 'for-me' ? (checkout.insurer.idNumber as string) : (checkout.insured.idNumber as string)

  const skipForMe =
    checkout.forWho === 'for-me' &&
    checkout.insurer.idNumber &&
    checkout.checkMyCar &&
    checkout.insuranceCar.registrationNumber &&
    insuredIdNumber &&
    insuredIdNumber.length === 11
      ? false
      : true

  const skipForOther =
    checkout.forWho === 'for-other' &&
    checkout.insurer.idNumber &&
    checkout.insurer.idNumber.length === 11 &&
    checkout.insured.idNumber &&
    checkout.insured.idNumber.length === 11 &&
    checkout.checkMyCar &&
    checkout.insuranceCar.registrationNumber &&
    insuredIdNumber &&
    insuredIdNumber.length === 11
      ? false
      : true

  const {
    data: tplCarInfo,
    isSuccess: tplCarInfoSuccess,
    isLoading: isTplCarInfoLoading,
    isFetching,
  } = api.useGetTplCarInfoQuery(
    {
      privateNumber: insuredIdNumber,
      registrationNumber: checkout.insuranceCar.registrationNumber || '',
      relatedPrivateNumber: checkout.forWho === 'for-other' ? (checkout.insurer.idNumber as string) : null,
    },
    {
      skip: checkout.forWho === 'for-me' ? skipForMe : skipForOther,
    },
  )

  const setCarInfoInStore = useCallback(() => {
    if (
      tplCarInfo &&
      tplCarInfo.brand &&
      tplCarInfo.vinCode &&
      checkout.insuranceCar.registrationNumber &&
      checkout.insuranceCar.registrationNumber.toLowerCase() === tplCarInfo.registrationNumber.toLowerCase()
    ) {
      // clear prev car info
      dispatch(clearTplCarInfo())
      // set new car info
      dispatch(
        setTplInsuranceCarInfo({
          techPassport: tplCarInfo.registrationNumber,
          vinCode: tplCarInfo.vinCode,
          carType: tplCarInfo.type,
          brand: tplCarInfo.brand,
          model: tplCarInfo.model,
          yearOfManufacture: tplCarInfo.releasedAt?.toString(),
          engineCapacity: tplCarInfo.engineCapacity?.toString(),
          registrationNumber: checkout.insuranceCar.registrationNumber,
        }),
      )
    }

    if (isFetching) {
      dispatch(setUpdateCarCountdown({ minute: 0, second: 30 }))
    }
  }, [dispatch, tplCarInfo, isFetching, checkout.insuranceCar.registrationNumber])

  // watch and set cur info in redux stroe
  useEffect(() => {
    setCarInfoInStore()
    return () => {}
  }, [setCarInfoInStore])

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (checkout.carCountdown.minute === 0 && checkout.carCountdown.second === 0 && isFetching) {
        clearInterval(intervalId)
        // Countdown complete, you can perform any action here
      } else {
        const newCountdown = {
          minute: checkout.carCountdown.minute,
          second: checkout.carCountdown.second,
        }

        if (checkout.carCountdown.minute === 0 && checkout.carCountdown.second <= 1) {
          newCountdown.second = 0
        } else {
          if (checkout.carCountdown.second === 0) {
            newCountdown.minute -= 1
            newCountdown.second = 59
          } else {
            newCountdown.second -= 1
          }
        }

        dispatch(setUpdateCarCountdown(newCountdown))
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }, [checkout.carCountdown.minute, checkout.carCountdown.second, isFetching, dispatch])

  // Display leading zeros for minutes and seconds
  const formattedTime =
    checkout.carCountdown.minute > 0
      ? `${String(checkout.carCountdown.minute).padStart(2, '0')}:${String(checkout.carCountdown.second).padStart(
          2,
          '0',
        )}`
      : `${String(checkout.carCountdown.second).padStart(2, '0')}`

  const calculateEngineDisplacement = (digit: string) => {
    if (digit && digit.length >= 1) {
      const displacement = Number(digit) / 1000
      const roundedDisplacement = displacement?.toFixed(1)
      return roundedDisplacement
    } else {
      return
    }
  }

  const carEngineCapacity = calculateEngineDisplacement(checkout.insuranceCar.engineCapacity)

  const registrationNumberIsValid = /^[a-zA-Z0-9\s]+$/.test(checkout.insuranceCar.registrationNumber)
  const timerIsVisible = checkout.carCountdown.minute === 0 && checkout.carCountdown.second === 0 ? false : true

  const MainLoading = isTplCarPageInfoLoading || isTplCarInfoLoading

  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageTitle} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />

      <Content>
        <Title text={pageTitle} />

        <TplProgressbar stepName={stepName} />

        <MovingPrice price={filter.offerTotalPrice} currency={filter.currency} />

        <WhiteBox>
          <Inputs>
            <CounterBlock
              style={{
                opacity: timerIsVisible ? '0.8' : 'unset',
              }}
            >
              <Input
                id="tpl-car-registration-number"
                name="tpl-car-registration-number"
                label={t('carRegisterNumber')}
                type="text"
                placeholder=""
                width={'fullWidth'}
                value={checkout.insuranceCar.registrationNumber?.toUpperCase() || ''}
                errorText={`${t('invalidCarRegistrationNumber')}`}
                error={!registrationNumberIsValid && checkout.insuranceCar.registrationNumber.trim() !== ''}
                onChange={(value) => [
                  dispatch(setCheckMyCar(false)),
                  dispatch(
                    setCarFields({
                      fieldName: 'registrationNumber',
                      fieldValue: value?.replaceAll('-', ''),
                    }),
                  ),
                ]}
                //
                disabled={timerIsVisible ? true : false}
              />

              <CounterDiv>{timerIsVisible ? <CounterSpan>{formattedTime}</CounterSpan> : null}</CounterDiv>
            </CounterBlock>

            <Checkbox
              style={{
                opacity: timerIsVisible ? '0.8' : 'unset',
              }}
              disabled={timerIsVisible ? true : false}
              id="check-number-terms"
              label={t('acceptToCheckCarInfo')}
              defaultChecked={checkout.checkMyCar || false}
              // checked={checkout.checkMyCar}
              onChange={(value: boolean) => {
                if (value === true) {
                  dispatch(setCheckMyCar(true))
                  //
                } else {
                  dispatch(setCheckMyCar(false))
                }
              }}
            />

            {tplCarInfo && !tplCarInfo.brand && !tplCarInfo.vinCode && checkout.checkMyCar ? (
              <Message variant="gray" text={`${t('notFountCarInfoForRegistrationNumber')}`} />
            ) : null}

            {tplCarInfo &&
              tplCarInfo.registrationNumber &&
              tplCarInfo.registrationNumber &&
              checkout.insuranceCar?.registrationNumber?.toLowerCase() ===
                tplCarInfo.registrationNumber?.toLowerCase() && (
                <>
                  <hr />

                  <Input
                    id="tpl-vin-code"
                    name="tpl-vin-code"
                    label={t('vinCode')}
                    type="text"
                    placeholder=""
                    value={checkout.insuranceCar.vinCode || ''}
                    onChange={(value) => {}}
                    width={'fullWidth'}
                    disabled={true}
                  />

                  <Input
                    id="tpl-tech-passport"
                    name="tpl-tech-passport"
                    label={t('techPassport')}
                    type="text"
                    placeholder=""
                    value={checkout.insuranceCar.techPassport || ''}
                    onChange={(value) => {}}
                    width={'fullWidth'}
                    disabled={true}
                  />

                  <Input
                    id="tpl-car-type"
                    name="tpl-car-type"
                    label={t('carType')}
                    type="text"
                    placeholder=""
                    value={checkout.insuranceCar.carType || ''}
                    onChange={(value) => {}}
                    width={'fullWidth'}
                    disabled={true}
                  />

                  <Input
                    id="tpl-car-brand"
                    name="tpl-car-brand"
                    label={t('brand')}
                    type="text"
                    placeholder=""
                    value={checkout.insuranceCar.brand || ''}
                    onChange={(value) => {}}
                    width={'fullWidth'}
                    disabled={true}
                  />

                  <Input
                    id="tpl-car-model"
                    name="tpl-car-model"
                    label={t('carModel')}
                    type="text"
                    placeholder=""
                    value={checkout.insuranceCar.model || ''}
                    onChange={(value) => {}}
                    width={'fullWidth'}
                    disabled={true}
                  />

                  <Input
                    id="tpl-car-relase-date"
                    name="tpl-car-relase-date"
                    label={t('manufactoreYear')}
                    type="text"
                    placeholder=""
                    value={checkout.insuranceCar.yearOfManufacture || ''}
                    onChange={(value) => {}}
                    width={'fullWidth'}
                    disabled={true}
                  />

                  <Input
                    id="tpl-car-engine-capacity"
                    name="tpl-car-engine-capacity"
                    label={t('engineCapacity')}
                    type="text"
                    placeholder=""
                    value={carEngineCapacity || ''}
                    onChange={(value) => {}}
                    width={'fullWidth'}
                    disabled={true}
                  />
                </>
              )}

            {/*  */}
          </Inputs>
        </WhiteBox>

        <Buttons>
          <NavigationButton
            text={`${t('back')}`}
            buttonType="prev"
            onClick={() => navigate(checkout.forWho === 'for-me' ? '/tpl-for-me' : '/tpl-insurer')}
          />

          <NavigationButton
            text={`${t('next')}`}
            buttonType="next"
            disabled={
              tplCarInfoSuccess &&
              tplCarInfo.registrationNumber?.toLowerCase() ===
                checkout.insuranceCar.registrationNumber?.toLowerCase() &&
              checkout.insuranceCar &&
              checkout.insuranceCar.brand &&
              checkout.insuranceCar.carType &&
              checkout.insuranceCar.engineCapacity &&
              checkout.insuranceCar.model &&
              checkout.insuranceCar.registrationNumber &&
              checkout.insuranceCar.techPassport &&
              checkout.insuranceCar.vinCode &&
              checkout.insuranceCar.yearOfManufacture
                ? false
                : true
            }
            onClick={() => navigate('/tpl-check-information')}
          />
        </Buttons>
      </Content>

      <BackgroundIcon />
    </Wrapper>
  )
}
//
const CounterDiv = styled.div`
  background-color: transparent;
  padding: 5px;
  position: absolute;
  top: 36px;
  right: 23px;
  font-size: 12px;
  color: ${Colors.primaryBlack};
  user-select: none;
  pointer-events: none;
`
const CounterSpan = styled.span`
  background-color: ${Colors.primaryRed};
  color: ${Colors.white};
  padding-inline: 5px;
  min-width: 30px;
  height: 30px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: -6px;
  margin-right: -6px;

  ${Responsive.mobile} {
    margin-top: -6px;
    margin-right: -19px;
  }
`
const CounterBlock = styled.div`
  position: relative;
  /*  */
`
const Inputs = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  grid-column-gap: 0px;
  grid-row-gap: 20px;
  margin-top: 20px;
`
const WhiteBox = styled.div`
  background-color: ${Colors.white};
  margin-block: 32px;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  max-width: 464px;
  margin-inline: auto;
  border-radius: 8px;
  padding: 48px;
  padding-top: 10px;

  ${Responsive.mobile} {
    padding: 20px;
  }
`

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`
const Content = styled.div`
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;
  width: 100%;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
`
const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;
  /*  */
`
//
export default TplCarInfo
